'use client';

import dayjs from 'dayjs';
import useSWR from 'swr';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { NyttFärgTema, ChipFärger } from 'src/theme/NyttFärgtema';
import { hämtaBankkonton } from 'src/sections/bank/BankService';
import { hämtaAllaKundfakturorInklBokförda, type IKundfakturaFörslag } from 'src/sections/bokforing/affarshandelser/skapa/kundfaktura/KundfakturaService';
import { hämtaSkattekontoutdrag } from 'src/sections/skatt/SkatteService';
import axiosInstance from 'src/lib/axios';
import { formatMomsperiod } from 'src/sections/moms/momsPeriod';
import type { IBankkonto } from 'src/types/nya/ekonomi/bank/BankTyper';
import type { SkattekontoutdragRespons } from 'src/types/nya/ekonomi/skatt/SkatteTyper';

import type { KortProps } from '../kort-katalog';
import DomainCard from '../components/domain-card';
import { MiniList, MiniRow, formatKr, parseSkattekontoSaldo, swrOptions } from './kort-primitiver';

// ----------------------------------------------------------------------

interface IMomsdeklaration {
    id?: string;
    period?: { förstaDag?: string; sistaDag?: string };
    sistaDagIPerioden?: string;
    inlämnadPå?: string | null;
}

export default function EkonomiKort({ orgId }: KortProps) {
    const { data: bankkonton = [], isLoading: bankLoading } = useSWR<IBankkonto[]>(
        orgId ? ['bankkonton', orgId] : null,
        () => hämtaBankkonton(orgId),
        swrOptions
    );

    const { data: kundfakturor = [], isLoading: fakturaLoading } = useSWR<IKundfakturaFörslag[]>(
        orgId ? ['kundfakturor-alla', orgId] : null,
        () => hämtaAllaKundfakturorInklBokförda(orgId),
        swrOptions
    );

    const { data: skattekontoutdrag, isLoading: skattLoading } = useSWR<SkattekontoutdragRespons>(
        orgId ? ['skattekontoutdrag', orgId] : null,
        () => hämtaSkattekontoutdrag(orgId),
        swrOptions
    );

    const { data: momsdeklarationer = [], isLoading: momsLoading } = useSWR<IMomsdeklaration[]>(
        orgId ? ['momsdeklarationer', orgId] : null,
        () => axiosInstance.get<IMomsdeklaration[]>(`/api/momsdeklaration/${orgId}`).then((r) => r.data ?? []),
        swrOptions
    );

    const loading = bankLoading || fakturaLoading || skattLoading || momsLoading;
    const theme = NyttFärgTema.Kombinationer[5];
    const href = paths.dashboard.ekonomi.root;

    if (loading && bankkonton.length === 0 && kundfakturor.length === 0) {
        return (
            <DomainCard title="Ekonomi" icon="solar:card-bold" theme={theme} href={href}
                primaryMetric={{ value: '—', label: 'totalt saldo' }}>
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    const totalSaldo = bankkonton.reduce((sum, k) => sum + (k.saldo ?? 0), 0);
    const obetalda = kundfakturor.filter((f) => !f.matchadBetalning).length;

    const rawTransaktioner = skattekontoutdrag?.parsning?.transaktioner ?? [];
    const bokföringsTransaktioner = (skattekontoutdrag?.skattekontoutdrag?.bokföringAvSkatteverketTransaktioner ?? [])
        .map((b) => b.skatteverkettransaktion)
        .filter(Boolean);
    const skatteTransaktioner = rawTransaktioner.length > 0 ? rawTransaktioner : bokföringsTransaktioner;
    const senasteSkatteTransaktion = [...skatteTransaktioner]
        .sort((a, b) => dayjs(b?.datum as any).valueOf() - dayjs(a?.datum as any).valueOf())[0];
    const skatteSaldo = parseSkattekontoSaldo(senasteSkatteTransaktion?.rådata);

    const senasteMoms = [...momsdeklarationer]
        .sort((a, b) => String(b.period?.sistaDag ?? '').localeCompare(String(a.period?.sistaDag ?? '')))[0];
    const momsInlämnad = senasteMoms?.inlämnadPå ? true : false;

    const harData = bankkonton.length > 0 || kundfakturor.length > 0 || skatteTransaktioner.length > 0 || momsdeklarationer.length > 0;

    if (!harData) {
        return (
            <DomainCard title="Ekonomi" icon="solar:card-bold" theme={theme} href={href}
                emptyMessage="Anslut bankkonto, ladda upp fakturor eller skattekontoutdrag för att se ekonomisk översikt." />
        );
    }

    return (
        <DomainCard
            title="Ekonomi"
            icon="solar:card-bold"
            theme={theme}
            href={href}
            primaryMetric={bankkonton.length > 0
                ? { value: formatKr(totalSaldo), label: 'banksaldo' }
                : kundfakturor.length > 0
                    ? { value: `${kundfakturor.length}`, label: 'kundfakturor' }
                    : { value: '—', label: 'översikt' }
            }
            secondaryMetric={obetalda > 0
                ? { value: `${obetalda}`, label: 'obetalda fakturor' }
                : undefined
            }
            statusChip={
                momsdeklarationer.length > 0
                    ? momsInlämnad
                        ? { label: 'MOMS INLÄMNAD', color: ChipFärger[5] }
                        : { label: 'MOMS EJ INLÄMNAD', color: ChipFärger[9] }
                    : undefined
            }
        >
            <MiniList>
                {bankkonton.length > 0 && (
                    <MiniRow
                        primary={`${bankkonton.length} ${bankkonton.length === 1 ? 'konto' : 'konton'}`}
                        secondary="Banksaldo"
                        trailing={formatKr(totalSaldo)}
                        trailingColor="success.main"
                    />
                )}
                {kundfakturor.length > 0 && (
                    <MiniRow
                        primary={`${kundfakturor.length} kundfakturor`}
                        secondary={obetalda > 0 ? `${obetalda} obetalda` : 'Alla betalda'}
                    />
                )}
                {skatteSaldo != null && (
                    <MiniRow
                        primary="Skattekonto"
                        trailing={formatKr(skatteSaldo)}
                        trailingColor={skatteSaldo >= 0 ? 'success.main' : 'error.main'}
                    />
                )}
                {senasteMoms && (
                    <MiniRow
                        primary={`Moms ${formatMomsperiod(senasteMoms.period)}`}
                        secondary={momsInlämnad ? 'Inlämnad' : 'Ej inlämnad'}
                    />
                )}
            </MiniList>
        </DomainCard>
    );
}
